import { CheckCircle2, CircleAlert, Search, ShieldAlert } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import type { InterferenceAnalysisResponse } from '@/lib/urm-api';

function hasSupportedWeight(analysis: InterferenceAnalysisResponse): boolean {
  return analysis.workload_costs.some((cost) =>
    cost.confidence_low != null && cost.confidence_high != null && cost.confidence.toLowerCase() !== 'low',
  );
}

export function AnalysisOverview({
  analysis,
  incidentCount,
}: {
  analysis: InterferenceAnalysisResponse;
  incidentCount: number;
}) {
  const supportedWeights = hasSupportedWeight(analysis);
  const usesMean = analysis.latency_statistic === 'mean';
  const hasLatencyAnomalies = analysis.events.length > 0;
  const hasCandidateIncrease = analysis.events.some((event) =>
    event.candidate_loads.some((load) => load.change_from_baseline_rps > 0),
  );
  const validatedSignal = hasCandidateIncrease && supportedWeights && analysis.validation.validated && !usesMean;
  const status = !hasLatencyAnomalies
    ? 'No meaningful anomaly'
    : !hasCandidateIncrease
      ? 'Latency anomaly only'
      : validatedSignal
        ? 'Validated interference signal'
        : 'Possible interference signal';
  const exploratory = !validatedSignal;

  return (
    <Card className={exploratory ? 'border-amber-300 dark:border-amber-900' : 'border-emerald-300 dark:border-emerald-900'}>
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-center gap-2">
          {exploratory ? <CircleAlert className="h-5 w-5 text-amber-600" /> : <CheckCircle2 className="h-5 w-5 text-emerald-600" />}
          <CardTitle>What this run means</CardTitle>
          <Badge variant="outline">{status}</Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="max-w-4xl text-sm leading-6">
          {!hasLatencyAnomalies
            ? 'No meaningful latency deviation was detected in this window.'
            : !hasCandidateIncrease
              ? `${incidentCount} latency-anomaly period${incidentCount === 1 ? ' was' : 's were'} detected, but no qualifying workload increase occurred beforehand. This window does not provide evidence of API interference.`
              : !supportedWeights
                ? `Workload traffic increased before some latency anomalies, but no reliable workload weights could be estimated. Treat this as a lead to investigate, not proof of interference.`
                : analysis.validation.validated
                  ? `${analysis.workload_costs.length} workload weight estimate${analysis.workload_costs.length === 1 ? '' : 's'} passed the available evidence and chronological validation checks.`
                  : 'Workload traffic increased before some latency anomalies, but the signal has not passed chronological validation.'}
        </p>
        <div className="grid gap-3 md:grid-cols-3">
          <OverviewItem
            icon={<Search className="h-4 w-4" />}
            label="Latency anomalies"
            value={`${incidentCount} time period${incidentCount === 1 ? '' : 's'}`}
            detail={`${analysis.events.length} user/workload observation${analysis.events.length === 1 ? '' : 's'} grouped by time; these are not separate incidents.`}
          />
          <OverviewItem
            icon={<CircleAlert className="h-4 w-4" />}
            label="Latency data"
            value={usesMean ? 'Temporary mean fallback' : 'p95 latency'}
            detail={usesMean ? 'Useful for exploration, but not comparable to the final p95 analysis.' : 'Using latency-distribution histograms.'}
          />
          <OverviewItem
            icon={<ShieldAlert className="h-4 w-4" />}
            label="Recommended next step"
            value={!hasLatencyAnomalies ? 'No action from this run' : validatedSignal ? 'Review the limiter simulation' : hasCandidateIncrease ? 'Collect a longer window' : 'Investigate latency separately'}
            detail={!hasLatencyAnomalies ? 'This window did not produce a signal to investigate.' : validatedSignal ? 'Check coverage against normal-traffic impact before applying anything.' : hasCandidateIncrease ? 'More independent periods are needed before changing rate-limit weights.' : 'Look for service, dependency, or infrastructure causes; do not change weights based on this run.'}
          />
        </div>
      </CardContent>
    </Card>
  );
}

function OverviewItem({ icon, label, value, detail }: { icon: React.ReactNode; label: string; value: string; detail: string }) {
  return (
    <div className="rounded-lg border bg-muted/20 p-3">
      <p className="flex items-center gap-2 text-xs font-medium text-muted-foreground">{icon}{label}</p>
      <p className="mt-2 font-semibold">{value}</p>
      <p className="mt-1 text-xs leading-5 text-muted-foreground">{detail}</p>
    </div>
  );
}
